// ResultDashboard.jsx - UPDATED WITH EDIT OPTION
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  LinearProgress,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Avatar,
  Badge,
  Divider,
} from '@mui/material';
import {
  Search,
  PlayArrow,
  CheckCircle,
  Visibility,
  Assignment,
  LocalHospital,
  Science,
  VerifiedUser,
  Refresh,
  MoreVert,
  Delete,
  Block,
  Undo,
  Print,
  Download,
  Email,
  Info,
  Person,
  Edit,
  Lock,
  LockOpen,
  Warning,
  Security,
  MedicalServices,
  CalendarToday,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { resultEntryService } from '../../../../../../src/services/resultEntryService';

export const ResultDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pendingTests, setPendingTests] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // User and permissions state
  const [currentUser, setCurrentUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({
    canVerify: false,
    canRelease: false,
    canCancel: false,
    canEditResults: true,
    canEditDates: true,
    canView: true,
    signoff: false,
    amend: false,
  });
  const [userAuthorizedSections, setUserAuthorizedSections] = useState([]);
  const [userAuthorizedDoctors, setUserAuthorizedDoctors] = useState([]);

  // Delete/Cancel states
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');

  // Date edit states
  const [dateEditDialog, setDateEditDialog] = useState(false);
  const [editDates, setEditDates] = useState({
    collectionDate: null,
    receivedDate: null,
    testedAt: null,
    reportedDate: null
  });

  // Load user info and permissions on mount
  useEffect(() => {
    loadUserInfo();
    loadPendingTests();
  }, [activeTab, searchTerm, statusFilter]);

  const loadUserInfo = async () => {
    try {
      // Get logged-in user from localStorage
      const userData = JSON.parse(localStorage.getItem('user'));
      setCurrentUser(userData);

      if (userData) {
        // Fetch user's technician profile and permissions
        const response = await resultEntryService.getTechnicianById(userData._id || userData.id);

        if (response.success && response.data) {
          const techData = response.data;

          setUserPermissions({
            canVerify: techData.permissions?.canVerify || false,
            canRelease: techData.permissions?.canRelease || false,
            canCancel: techData.permissions?.canCancel || false,
            canEditResults: techData.permissions?.canEditResults !== false,
            canEditDates: techData.permissions?.canEditDates !== false,
            canView: true,
            signoff: techData.signoff === true || techData.signoff === 'true',
            amend: techData.amend === true || techData.amend === 'true',
          });

          // Set authorized sections
          setUserAuthorizedSections(techData.authorizedSections || []);

          // Set authorized doctors
          setUserAuthorizedDoctors(techData.doctors || []);
        }
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  const loadPendingTests = async () => {
    setLoading(true);
    setError('');
    try {
      const statusMap = {
        0: 'pending',
        1: 'testing',
        2: 'completed',
        3: 'verified',
        4: 'cancelled',
        5: 'deleted'
      };

      const status = statusMap[activeTab];
      const response = await resultEntryService.getPendingTests(1, 50, searchTerm, status);

      if (response.success) {
        setAllTests(response.data);
        setPendingTests(response.data);
      } else {
        setError(response.message || 'Failed to load pending tests');
      }
    } catch (error) {
      console.error('Error loading pending tests:', error);
      setError(error.response?.data?.message || 'Failed to load pending tests');
      showSnackbar('Error loading pending tests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      loadPendingTests();
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const hasPermission = (action) => {
    if (!currentUser) return false;

    // Admin / SuperAdmin users have all permissions
    if (currentUser?.role?.toLowerCase() === "admin" || currentUser?.superadmin) {
      return true;
    }
    // Check specific permission from userPermissions state
    return userPermissions[action] || false;
  };

  const isTestInAuthorizedSection = (test) => {
    if (!userAuthorizedSections.length) return true; // No restrictions if no sections assigned

    const testSection = test.service_details?.section || test.service_results?.[0]?.service?.section;
    return userAuthorizedSections.includes(testSection) || userAuthorizedSections.includes('General');
  };

  // ==================== EDIT FUNCTIONS ====================
  const handleEditTest = (test) => {
    const status = getTestStatus(test);
    const serviceId = getServiceId(test);

    if (status === 'cancelled' || status === 'deleted') {
      showSnackbar('Cannot edit cancelled or deleted tests', 'warning');
      return;
    }

    // For released/verified tests → require AMEND permission
    if (status === 'released' || status === 'verified') {
      if (!hasPermission('amend')) {
        showSnackbar('You need Amend permission to edit released/verified tests', 'error');
        return;
      }
    } else {
      // For pending/in_progress/for_verification → require canEditResults
      if (!hasPermission('canEditResults')) {
        showSnackbar('You do not have permission to edit results', 'error');
        return;
      }
    }

    // Check if test is in authorized section
    if (!isTestInAuthorizedSection(test)) {
      showSnackbar('You are not authorized to edit this test', 'error');
      return;
    }

    // Navigate to edit page
    navigate(`/hospital/laboratory/result-entry/result-dashboard/${test._id}`, {
      state: {
        serviceId: serviceId,
        mode: 'edit'
      }
    });
  };

  const handleEditDates = (test) => {
    if (!hasPermission('canEditDates')) {
      showSnackbar('You do not have permission to edit dates', 'error');
      return;
    }

    if (!isTestInAuthorizedSection(test)) {
      showSnackbar('You are not authorized to edit this test', 'error');
      return;
    }

    setSelectedTest(test);

    // Set current dates for editing
    const service = test.service_results?.[0];
    setEditDates({
      collectionDate: service?.collectionDate || test.collection_date || null,
      receivedDate: service?.receivedDate || test.received_date || null,
      testedAt: service?.testedAt || null,
      reportedDate: test.reported_date || null
    });

    setDateEditDialog(true);
  };

  const saveEditedDates = async () => {
    if (!selectedTest) return;

    setLoading(true);
    try {
      // Prepare date updates
      const dateUpdates = {};

      if (editDates.collectionDate) {
        dateUpdates.collection_date = editDates.collectionDate;
      }
      if (editDates.receivedDate) {
        dateUpdates.received_date = editDates.receivedDate;
      }
      if (editDates.reportedDate) {
        dateUpdates.reported_date = editDates.reportedDate;
      }

      // If there's a service, update service dates too
      const service = selectedTest.service_results?.[0];
      if (service && editDates.testedAt) {
        const serviceUpdates = {
          testedAt: editDates.testedAt
        };

        await resultEntryService.updateServiceDates(
          selectedTest._id,
          service._id,
          serviceUpdates
        );
      }

      // Update main result dates
      if (Object.keys(dateUpdates).length > 0) {
        await resultEntryService.updateResultDates(selectedTest._id, dateUpdates);
      }

      showSnackbar('Dates updated successfully', 'success');
      setDateEditDialog(false);
      loadPendingTests();
    } catch (error) {
      console.error('Error updating dates:', error);
      showSnackbar('Failed to update dates', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== ACTION MENU FUNCTIONS ====================
  const handleActionMenuOpen = (event, test) => {
    event.stopPropagation();
    setSelectedTest(test);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedTest(null);
  };

  const cancelTestReport = async () => {
    if (!selectedTest) return;

    if (!hasPermission('canCancel')) {
      showSnackbar('You do not have permission to cancel tests', 'error');
      return;
    }

    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const cancellationData = {
        cancelledBy: user?.SuperAdminId || user?.id,
        cancellationReason: cancellationReason || 'Cancelled by user',
        cancelledAt: new Date().toISOString()
      };

      const response = await resultEntryService.cancelTestReport(selectedTest._id, cancellationData);

      if (response.success) {
        showSnackbar('Test report cancelled successfully', 'success');
        loadPendingTests();
        setCancelDialog(false);
        setCancellationReason('');
      } else {
        showSnackbar(response.message || 'Failed to cancel test report', 'error');
      }
    } catch (error) {
      console.error('Error cancelling test report:', error);
      showSnackbar('Failed to cancel test report', 'error');
    } finally {
      setLoading(false);
      handleActionMenuClose();
    }
  };

  const deleteTestReport = async () => {
    if (!selectedTest) return;

    if (!hasPermission('canCancel')) {
      showSnackbar('You do not have permission to delete tests', 'error');
      return;
    }

    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const deleteData = {
        deletedBy: user?.SuperAdminId || user?.id,
        deleteReason: deleteReason || 'Deleted by user',
        deletedAt: new Date().toISOString()
      };

      const response = await resultEntryService.softDeleteTestReport(selectedTest._id, deleteData);

      if (response.success) {
        showSnackbar('Test report deleted successfully', 'success');
        loadPendingTests();
        setDeleteDialog(false);
        setDeleteReason('');
      } else {
        showSnackbar(response.message || 'Failed to delete test report', 'error');
      }
    } catch (error) {
      console.error('Error deleting test report:', error);
      showSnackbar('Failed to delete test report', 'error');
    } finally {
      setLoading(false);
      handleActionMenuClose();
    }
  };

  const restoreTestReport = async (test) => {
    if (!hasPermission('canCancel')) {
      showSnackbar('You do not have permission to restore tests', 'error');
      return;
    }

    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const restoreData = {
        restoredBy: user?.SuperAdminId || user?.id,
        restoredAt: new Date().toISOString()
      };

      let response;
      if (test.cancelled) {
        response = await resultEntryService.restoreTestReport(test._id, restoreData);
      } else if (test.deleted) {
        response = await resultEntryService.restoreDeletedTestReport(test._id, restoreData);
      }

      if (response?.success) {
        showSnackbar('Test report restored successfully', 'success');
        loadPendingTests();
      } else {
        showSnackbar(response?.message || 'Failed to restore test report', 'error');
      }
    } catch (error) {
      console.error('Error restoring test report:', error);
      showSnackbar('Failed to restore test report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStartResultEntry = async (resultId, serviceId) => {
    try {
      setLoading(true);
      navigate(`/hospital/laboratory/result-entry/result-dashboard/${resultId}`, {
        state: {
          serviceId: serviceId,
        }
      });
    } catch (error) {
      console.error('Error starting result entry:', error);
      showSnackbar('Failed to start result entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = (testId) => {
    navigate(`/hospital/laboratory/test-report/result-dashboard/${testId}`);
  };

  const handleVerifyResults = async (test) => {
    if (!hasPermission('canVerify') && !hasPermission('signoff')) {
      showSnackbar('You need Verify or SignOff permission to verify results', 'error');
      return;
    }

    navigate(`/hospital/laboratory/result-entry/result-dashboard/${test._id}`);
  };

  const handleReleaseResults = async (test) => {
    if (!hasPermission('canRelease') && !hasPermission('signoff')) {
      showSnackbar('You need Release or SignOff permission to release results', 'error');
      return;
    }

    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      const releaseData = {
        releasedBy: user?.SuperAdminId || user?.id,
        releasedAt: new Date().toISOString()
      };

      const response = await resultEntryService.releaseResults(test._id, releaseData);

      if (response.success) {
        showSnackbar('Results released successfully', 'success');
        loadPendingTests();
      } else {
        showSnackbar(response.message || 'Failed to release results', 'error');
      }
    } catch (error) {
      console.error('Error releasing results:', error);
      showSnackbar('Failed to release results', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTestStatus = (test) => {
    const serviceResults = Array.isArray(test.service_results)
      ? test.service_results
      : [test.service_results];

    if (test.cancelled) return 'cancelled';
    if (test.deleted) return 'deleted';
    if (serviceResults.every(sr => sr.status === 'released')) return 'released';
    if (serviceResults.every(sr => sr.status === 'verified')) return 'verified';
    if (serviceResults.some(sr => sr.status === 'completed')) return 'for_verification';
    if (serviceResults.some(sr => sr.status === 'testing')) return 'in_progress';
    if (serviceResults.some(sr => sr.status === 'pending')) return 'pending';
    return 'not_started';
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: 'warning', label: 'Ready for Testing', icon: <Science /> },
      in_progress: { color: 'info', label: 'In Progress', icon: <PlayArrow /> },
      for_verification: { color: 'primary', label: 'For Verification', icon: <VerifiedUser /> },
      verified: { color: 'success', label: 'Verified', icon: <CheckCircle /> },
      released: { color: 'secondary', label: 'Released', icon: <Print /> },
      cancelled: { color: 'error', label: 'Cancelled', icon: <Block /> },
      deleted: { color: 'default', label: 'Deleted', icon: <Delete /> },
      not_started: { color: 'default', label: 'Not Started', icon: <Assignment /> }
    };
    return configs[status] || configs.not_started;
  };

  const getServiceName = (test) => {
    if (test.service_details?.test_name) {
      return test.service_details.test_name;
    }
    if (Array.isArray(test.service_results)) {
      return test.service_results.map(sr => sr.service?.test_name || 'Unknown Test').join(', ');
    }
    return test.service_results?.service?.test_name || 'Unknown Test';
  };

  const getServiceId = (test) => {
    if (Array.isArray(test.service_results)) {
      return test.service_results[0]?.service?._id || test.service_results[0]?.service;
    }
    return test.service_results?.service?._id || test.service_results?.service;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canEditTest = (test) => {
    const status = getTestStatus(test);
    // For released/verified tests, check amend permission
    if (status === 'released' || status === 'verified') {
      return hasPermission('amend') && isTestInAuthorizedSection(test);
    }
    // For other editable statuses, check canEditResults
    const isEditable = status === 'pending' || status === 'in_progress' || status === 'for_verification';
    return hasPermission('canEditResults') && isEditable && isTestInAuthorizedSection(test);
  };

  const canEditTestDates = (test) => {
    const status = getTestStatus(test);
    const isNotReleased = status !== 'released' && !test.cancelled && !test.deleted;
    return hasPermission('canEditDates') && isNotReleased && isTestInAuthorizedSection(test);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* User Info Card - if you have this component */}
      {/* {currentUser && renderUserInfoCard()} */}

      <Card elevation={3}>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" gutterBottom color="primary">
              Result Entry Dashboard
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Manage laboratory test results and reporting
            </Typography>
          </Box>

          {/* Search and Filter Section */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by Barcode, Patient Name, or UHID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="for_verification">For Verification</MenuItem>
                    <MenuItem value="verified">Verified</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="deleted">Deleted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={loadPendingTests}
                  startIcon={<Refresh />}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Tabs Section */}
          <AppBar position="static" color="default" elevation={1}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Pending Tests" icon={<Assignment />} iconPosition="start" />
              <Tab label="In Progress" icon={<Science />} iconPosition="start" />
              <Tab label="For Verification" icon={<VerifiedUser />} iconPosition="start" />
              <Tab label="Completed" icon={<CheckCircle />} iconPosition="start" />
              <Tab label="Cancelled" icon={<Block />} iconPosition="start" />
              <Tab label="Deleted" icon={<Delete />} iconPosition="start" />
            </Tabs>
          </AppBar>

          {/* Tests Table */}
          {loading ? (
            <LinearProgress sx={{ mt: 2 }} />
          ) : (
            <TableContainer component={Paper} elevation={1} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Barcode</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Patient Info</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Doctor</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Test</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Dates</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingTests.map((test) => {
                    const status = getTestStatus(test);
                    const statusConfig = getStatusConfig(status);
                    const serviceName = getServiceName(test);
                    const serviceId = getServiceId(test);

                    // Permission checks
                    const canAccessTest = isTestInAuthorizedSection(test);
                    const canEdit = canEditTest(test);
                    const canEditDates = canEditTestDates(test);

                    return (
                      <TableRow
                        key={test._id}
                        hover
                        sx={{
                          backgroundColor: !canAccessTest ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                          opacity: !canAccessTest ? 0.7 : 1,
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {test.barcode_no}
                            </Typography>
                            {!canAccessTest && (
                              <Tooltip title="Not authorized for this section">
                                <Lock color="warning" fontSize="small" />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {test.uhid}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {test.patient_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {test.age} • {test.gender}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {test.doctor?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={serviceName}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(test.createdAt)}
                            </Typography>
                            {canEditDates && (
                              <Tooltip title="Edit dates">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditDates(test)}
                                  sx={{ p: 0, width: 'fit-content' }}
                                >
                                  <CalendarToday fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={statusConfig.label}
                              color={statusConfig.color}
                              size="small"
                              icon={statusConfig.icon}
                            />
                            {test.cancelled && test.cancellationReason && (
                              <Tooltip title={test.cancellationReason}>
                                <Info color="error" fontSize="small" />
                              </Tooltip>
                            )}
                            {test.deleted && test.deleteReason && (
                              <Tooltip title={test.deleteReason}>
                                <Info color="action" fontSize="small" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* Edit Button - for pending/in_progress/for_verification (requires canEditResults) */}
                            {(status === 'pending' || status === 'in_progress' || status === 'for_verification') && hasPermission('canEditResults') && canAccessTest && (
                              <Tooltip title="Edit test results">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Edit />}
                                  onClick={() => handleEditTest(test)}
                                  sx={{ minWidth: 'auto' }}
                                >
                                  Edit
                                </Button>
                              </Tooltip>
                            )}

                            {/* Amend Button - for released/verified (requires amend permission) */}
                            {(status === 'released' || status === 'verified') && hasPermission('amend') && canAccessTest && (
                              <Tooltip title="Amend released/verified results">
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  startIcon={<Edit />}
                                  onClick={() => handleEditTest(test)}
                                  sx={{ minWidth: 'auto' }}
                                >
                                  Amend
                                </Button>
                              </Tooltip>
                            )}

                            {/* Start/Verify Button */}
                            {status === 'pending' && hasPermission('canEditResults') && canAccessTest && (
                              <Tooltip title="Start result entry">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<PlayArrow />}
                                  onClick={() => handleStartResultEntry(test._id, serviceId)}
                                  disabled={loading}
                                  sx={{ minWidth: 'auto' }}
                                >
                                  Start
                                </Button>
                              </Tooltip>
                            )}

                            {status === 'in_progress' && hasPermission('canEditResults') && canAccessTest && (
                              <Tooltip title="Continue editing">
                                <Button
                                  variant="contained"
                                  color="info"
                                  size="small"
                                  startIcon={<PlayArrow />}
                                  onClick={() => handleStartResultEntry(test._id, serviceId)}
                                  disabled={loading}
                                  sx={{ minWidth: 'auto' }}
                                >
                                  Continue
                                </Button>
                              </Tooltip>
                            )}

                            {status === 'for_verification' && (hasPermission('canVerify') || hasPermission('signoff')) && canAccessTest && (
                              <Tooltip title="Verify results">
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={<VerifiedUser />}
                                  onClick={() => handleVerifyResults(test)}
                                  disabled={loading}
                                  sx={{ minWidth: 'auto' }}
                                >
                                  Verify
                                </Button>
                              </Tooltip>
                            )}

                            {/* View Report Button */}
                            {(status === 'verified' || status === 'released') && (
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => handleViewReport(test._id)}
                                sx={{ minWidth: 'auto' }}
                              >
                                View
                              </Button>
                            )}

                            {/* Release Button */}
                            {status === 'verified' && (hasPermission('canRelease') || hasPermission('signoff')) && canAccessTest && (
                              <Tooltip title="Release report">
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  size="small"
                                  startIcon={<Print />}
                                  onClick={() => handleReleaseResults(test)}
                                  disabled={loading}
                                  sx={{ minWidth: 'auto' }}
                                >
                                  Release
                                </Button>
                              </Tooltip>
                            )}

                            {/* Restore Button for cancelled/deleted */}
                            {(status === 'cancelled' || status === 'deleted') && hasPermission('canCancel') && (
                              <Tooltip title="Restore test report">
                                <Button
                                  variant="outlined"
                                  color="success"
                                  size="small"
                                  startIcon={<Undo />}
                                  onClick={() => restoreTestReport(test)}
                                  disabled={loading}
                                  sx={{ minWidth: 'auto' }}
                                >
                                  Restore
                                </Button>
                              </Tooltip>
                            )}

                            {/* More Actions Menu */}
                            {status !== 'cancelled' && status !== 'deleted' && hasPermission('canCancel') && canAccessTest && (
                              <Tooltip title="More actions">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleActionMenuOpen(e, test)}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && pendingTests.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LocalHospital sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No tests found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {activeTab === 4 ? 'No cancelled tests found' :
                  activeTab === 5 ? 'No deleted tests found' :
                    'No tests matching your criteria'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      {hasPermission('canCancel') && (
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
        >
          <MenuItem
            onClick={() => {
              handleActionMenuClose();
              setCancelDialog(true);
            }}
            disabled={selectedTest?.overall_status === 'released'}
          >
            <ListItemIcon>
              <Block fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cancel Test</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleActionMenuClose();
              setDeleteDialog(true);
            }}
            disabled={selectedTest?.overall_status === 'released'}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Test</ListItemText>
          </MenuItem>
        </Menu>
      )}

      {/* Date Edit Dialog */}
      <Dialog
        open={dateEditDialog}
        onClose={() => setDateEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday color="primary" />
            Edit Test Dates
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Patient: {selectedTest?.patient_name} • Barcode: {selectedTest?.barcode_no}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Collection Date"
              type="datetime-local"
              value={editDates.collectionDate ? new Date(editDates.collectionDate).toISOString().slice(0, 16) : ''}
              onChange={(e) => setEditDates({ ...editDates, collectionDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Received Date"
              type="datetime-local"
              value={editDates.receivedDate ? new Date(editDates.receivedDate).toISOString().slice(0, 16) : ''}
              onChange={(e) => setEditDates({ ...editDates, receivedDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Tested Date"
              type="datetime-local"
              value={editDates.testedAt ? new Date(editDates.testedAt).toISOString().slice(0, 16) : ''}
              onChange={(e) => setEditDates({ ...editDates, testedAt: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Reported Date"
              type="datetime-local"
              value={editDates.reportedDate ? new Date(editDates.reportedDate).toISOString().slice(0, 16) : ''}
              onChange={(e) => setEditDates({ ...editDates, reportedDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveEditedDates}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Dates'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialog}
        onClose={() => setCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
            <Block />
            Cancel Test Report
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will mark the test report as cancelled.
          </Alert>
          <Typography variant="body1" gutterBottom>
            Cancel test for: <strong>{selectedTest?.patient_name} - {selectedTest?.barcode_no}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Cancellation Reason"
            multiline
            rows={3}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Enter reason for cancellation..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={cancelTestReport}
            disabled={loading || !cancellationReason.trim()}
          >
            {loading ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <Delete />
            Delete Test Report
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will mark the test report as deleted.
          </Alert>
          <Typography variant="body1" gutterBottom>
            Delete test for: <strong>{selectedTest?.patient_name} - {selectedTest?.barcode_no}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Deletion Reason"
            multiline
            rows={3}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Enter reason for deletion..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={deleteTestReport}
            disabled={loading || !deleteReason.trim()}
          >
            {loading ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};